import { featureEnabled, roleCanAccess } from '../../services/features';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Event,
  CalendarMonth,
  Build,
  People,
  Business,
  Settings,
  AccountCircle,
  ExitToApp,
  FolderOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState, AppDispatch } from '../../store';
import { logout } from '../../store/slices/authSlice';
import OrganizationBrand from './OrganizationBrand';

const drawerWidth = 240;
const toolbarSx = { minHeight: '64px !important', pt: 'env(safe-area-inset-top)' };

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  
  const { user, tenant } = useSelector((state: RootState) => state.auth);
  const hasSidebar = user?.role !== 'super_admin';
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => { setMobileOpen(false); }, [location.pathname, desktop]);

  const handleDrawerToggle = () => {
    setMobileOpen(open => !open);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleProfileMenuClose();
    navigate('/login');
  };

  const navigationItems = user?.role === 'super_admin' ? [{ key: 'admin', label: 'Organizații', icon: <Business />, path: '/admin' }] : user?.role === 'client' ? [
    { key: 'clientPortal', label: 'Programările mele', icon: <Event />, path: '/my-appointments' },
    { key: 'clientCalendar', label: 'Calendar', icon: <CalendarMonth />, path: '/my-calendar' },
    { key: 'clientProfile', label: 'Profil', icon: <AccountCircle />, path: '/my-profile' },
    { key: 'clientDocuments', label: 'Acte', icon: <FolderOutlined />, path: '/my-documents' },
  ] : [
    { key: 'dashboard', label: t('nav.dashboard'), icon: <Dashboard />, path: '/dashboard' },
    { key: 'appointments', label: t('nav.appointments'), icon: <Event />, path: '/appointments' },
    { key: 'calendar', label: t('nav.calendar'), icon: <CalendarMonth />, path: '/calendar' },
    { key: 'services', label: t('nav.services'), icon: <Build />, path: '/services' },
    { key: 'clients', label: tenant?.businessType==='driving_school'&&user?.role==='staff'?'Clienții mei':'Clienți', icon: <People />, path: '/clients' },
    { key: 'staff', label: tenant?.businessType==='driving_school' ? 'Instructori și secretariat' : t('nav.staff'), icon: <People />, path: '/staff' },
    { key: 'resources', label: tenant?.businessType==='driving_school' ? 'Vehicule' : t('nav.resources'), icon: <Business />, path: '/resources' },
    { key: 'settings', label: t('nav.settings'), icon: <Settings />, path: '/settings' },
  ].filter(item => roleCanAccess(user?.role,item.key) && (item.key === 'dashboard' || featureEnabled(tenant, item.key))).flatMap(item => item.key==='calendar' && tenant?.businessType==='driving_school' && (!tenant.drivingMode || tenant.drivingMode==='auto_moto') ? [{...item,key:'calendarAuto',label:'Calendar Auto',path:'/calendar/auto'},{...item,key:'calendarMoto',label:'Calendar Moto',path:'/calendar/moto'}] : [item]);

  const drawer = (
    <Box sx={{display:'flex',flexDirection:'column',height:'100%',minHeight:0}}>
      <Toolbar sx={{...toolbarSx,justifyContent:'center',flexShrink:0,py:1.5,px:2,minHeight:'88px !important'}}>
        <OrganizationBrand name={tenant?.name || 'Orarivo · Administrare'} logoUrl={tenant?.logoUrl} />
      </Toolbar>
      <Divider />
      <List sx={{px:{xs:1.5,md:0},py:1.5,overflowY:'auto',flex:1}}>
        {navigationItems.map((item) => (
          <ListItem key={item.key} disablePadding>
            <ListItemButton
              aria-current={location.pathname === item.path || (item.key==='clients' && location.pathname.startsWith('/clients/')) ? 'page' : undefined}
              selected={location.pathname === item.path || (item.key==='clients' && location.pathname.startsWith('/clients/'))}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              sx={{ minHeight:52, borderRadius:{xs:2,md:0},mb:{xs:.5,md:0},'& .MuiListItemIcon-root':{minWidth:40},
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '15',
                  '& .MuiListItemIcon-root': {
                    color: theme.palette.primary.main,
                  },
                  '& .MuiListItemText-primary': {
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  },
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{display:{xs:'block',md:'none'},p:2,pb:'max(16px, env(safe-area-inset-bottom))',borderTop:'1px solid',borderColor:'divider',flexShrink:0}}><Typography variant="body2" sx={{fontWeight:600,overflowWrap:'anywhere'}}>{user?.fullName}</Typography><Typography variant="caption" color="text.secondary">{user?.role==='client'?'Cont elev':user?.role==='staff'?'Instructor':user?.role==='secretary'?'Secretariat':'Administrator'}</Typography></Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: hasSidebar ? { md: `calc(100% - ${drawerWidth}px)` } : '100%',
          ml: hasSidebar ? { md: `${drawerWidth}px` } : 0,
        }}
      >
        <Toolbar sx={{...toolbarSx,px:{xs:2,sm:3},gap:1}}>
          {hasSidebar && <IconButton
            color="inherit"
            aria-label="Deschide meniul"
            aria-expanded={mobileOpen}
            aria-controls={mobileOpen ? "mobile-navigation" : undefined}
            aria-haspopup="true"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 0, display: { xs: 'inline-flex', md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>}
          
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, minWidth: 0, fontSize: { xs: '1rem', sm: '1.25rem' }, lineHeight:1.3,overflowWrap:'anywhere' }}>
            {hasSidebar ? navigationItems.find(item => item.path === location.pathname)?.label || (location.pathname==='/profile'?'Profil':location.pathname.startsWith('/clients/')?'Dosarul clientului':'Panou Administrare') : 'Orarivo · Administrare'}
          </Typography>

          <IconButton
            size="large"
            aria-label="Contul meu"
            aria-controls="profile-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)' }}>
              {user?.fullName?.charAt(0) || <AccountCircle />}
            </Avatar>
          </IconButton>
          
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            {user?.role === 'client' && <MenuItem onClick={() => { navigate('/my-profile'); handleProfileMenuClose(); }}>
              <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
              Profil
            </MenuItem>}
            {user?.role !== 'super_admin' && user?.role !== 'client' && <MenuItem onClick={() => { navigate('/profile'); handleProfileMenuClose(); }}>
              <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>Profil
            </MenuItem>}
            {user?.role !== 'super_admin' && roleCanAccess(user?.role,'settings') && featureEnabled(tenant, 'settings') && <MenuItem onClick={() => { navigate('/settings'); handleProfileMenuClose(); }}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              {t('nav.settings')}
            </MenuItem>}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              {t('nav.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {hasSidebar && <Box
        component="nav"
        aria-label="Meniul principal"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          anchor="left"
          onClose={() => setMobileOpen(false)}
          transitionDuration={{enter:240,exit:180}}
          PaperProps={{id:"mobile-navigation",'aria-label':"Navigație principală"}}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width:'min(320px, calc(100vw - 48px))',height:'100dvh',border:0,borderRadius:'0 16px 16px 0' },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="persistent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: { xs: 2, sm: 3 },
          pb: {xs:'max(24px, env(safe-area-inset-bottom))',sm:3},
          width: hasSidebar ? { md: `calc(100% - ${drawerWidth}px)` } : '100%',
        }}
      >
        <Toolbar sx={toolbarSx}/>
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;
